import request from 'utils/header'
import requestv2 from 'utils/realtime'
import requestv3 from 'utils/cron'
import { createDedupedRequest } from '../requestDedup'
import { getProcessFlow, getCachedProcessFlow as getCachedProcessFlowImpl, clearProcessFlowCache as clearProcessFlowCacheImpl, registerFetcher } from '../processFlowDispatch'
import { getGroupStore as getGroupStoreImpl, getCachedGroupStore as getCachedGroupStoreImpl, clearGroupStoreCache as clearGroupStoreCacheImpl, registerFetcher as registerGroupStoreFetcher } from '../groupStoreDispatch'
import { formatISO } from 'date-fns'
import axios from 'axios'

// const get_users = (_type) =>
//   request
//     .get('/users')
//     .then((users) => {
//       let newUser = []
//       if (!users.data) return newUser

//       users.data.map((v, k) => {
//         if (v.role !== _type) return ''
//         if (v.firstName === null || v.lastName === null) return ''
//         return newUser.push({...v})
//       })

//       return {
//         ok: true,
//         data: newUser,
//       }
//     })
//     .catch((err) => {
//       return {ok: false, data: err}
//     })

// Helper: build axios/apisauce config with optional AbortSignal
const withSignal = (options) => (options?.signal != null ? { signal: options.signal } : {})

// Centralized user cache: one fetch per user per session; refetch only on refresh or after clear
const userCache = { id: null, data: null }

/**
 * After login/register, the profile is already in the auth response. Seed the same shape as `get_user`
 * so the next `get_user` does not fire a duplicate GET until `forceRefresh` or `clearUserCache`.
 */
const seedUserCacheFromLoginPayload = (payload) => {
  try {
    if (!payload || typeof payload !== 'object') return
    const rawId = payload._id !== undefined && payload._id !== null ? payload._id : payload.id
    if (rawId === undefined || rawId === null || String(rawId).trim() === '') return
    if (payload.role === undefined || payload.role === null) return
    const id = String(rawId)
    const data = { ...payload }
    delete data.token
    if (data._id === undefined || data._id === null) data._id = rawId
    userCache.id = id
    userCache.data = { ok: true, data }
  } catch (_e) {
    // ignore malformed payloads
  }
}


const get_user = async (_id, options = {}) => {
  const id = _id != null ? String(_id) : ''
  const config = withSignal(options)
  const forceRefresh = Boolean(options.forceRefresh)
  if (!id) return requestv2.get(`/user/${_id}`, null, config)
  if (!forceRefresh && userCache.id === id && userCache.data) return userCache.data
  const res = await requestv2.get(`/user/${_id}`, null, config)
  if (res?.ok && res?.data) {
    userCache.id = id
    userCache.data = res
  }
  return res
}

const clearUserCache = () => {
  userCache.id = null
  userCache.data = null
}

const get_user_by_id = (_id, options = {}) => requestv2.get(`/user/${_id}`, null, withSignal(options))

const get_user_status_deduped = createDedupedRequest(
  (p) => `status:${p._id}`,
  (p) => requestv2.get(`/user/status/${p._id}`, null, withSignal(p))
)
const get_user_status = (_id, options = {}) => get_user_status_deduped({ _id, ...options })

const check_user_archived = (_id, options = {}) => requestv2.get(`/user/${_id}/archived`, null, withSignal(options))

const get_user_archived = (_id, options = {}) => requestv2.get(`/user/${_id}/archived`, null, withSignal(options))

const create_new_password = (options = {}) => request.get(`/user/generate/password`, null, withSignal(options))

const validate_password = (_id, options = {}) => request.get(`/user/validate/password/${_id}`, null, withSignal(options))

const get_limited_users_deduped = createDedupedRequest(
  (p) => `limited:${p._id}`,
  (p) => requestv2.get(`/user/limited/records/${p._id}`, null, withSignal(p))
)
const get_limited_users = (_id, options = {}) => get_limited_users_deduped({ _id, ...options })

const get_limited_usersV2 = (options = {}) => request.get(`/user/limited/recordsV2`, null, withSignal(options))

const get_limited_usersV3 = (company, search = 'all', options = {}) => request.post(`/user/limited/recordsV3`, { company, search }, withSignal(options))

const get_limited_usersV4_deduped = createDedupedRequest(
  (p) => `limitedV4:${p.userId ?? ''}:${p.limit ?? 5}`,
  (p) => request.post(`/user/limited/recordsV4`, { userId: p.userId, limit: p.limit ?? 5 }, withSignal(p))
)
const get_limited_usersV4 = (userId, limit = 5, options = {}) => get_limited_usersV4_deduped({ userId, limit, ...options })

const get_record_by_id = (_id, options = {}) => request.get(`/record/${_id}`, null, withSignal(options))

const delete_record_by_id = (_id, options = {}) => request.get(`/record/delete/${_id}`, null, withSignal(options))

const delete_last_record_by_id = (_id, options = {}) => request.get(`/record/delete/last/${_id}`, null, withSignal(options))

const delete_demo_last_record = (_id, options = {}) => request.get(`/record/delete/last/demo/${_id}`, null, withSignal(options))


const update_record_by_id = (_id, _data, options = {}) => request.patch(`/v2/record/update/${_id}`, _data, withSignal(options))

const get_users_bydate = (_id, _date, options = {}) => request.get(`/user/records/bydate/${_id}/${_date}`, null, withSignal(options))

const get_users = (_id, options = {}) => request.get(`/store/users/${_id}`, null, withSignal(options))

const get_users_archived = (_id, options = {}) => request.get(`/store/users/archive/${_id}`, null, withSignal(options))

const archive_user = (store_id, user_id, options = {}) => request.get(`/store/${store_id}/user/${user_id}/archive`, null, withSignal(options))

const archive_user_v2 = (_data, options = {}) => requestv2.post(`/store/user/archive`, _data, withSignal(options))

const restore_user = (store_id, user_id, options = {}) => request.get(`/store/${store_id}/user/${user_id}/restore`, null, withSignal(options))

const check_registration_company = (_data, options = {}) => request.post(`/register/company/check`, _data, withSignal(options))

const post_store_register = async (_data, options = {}) => {
  if (Number(_data?.role) !== 1) {
    return {
      ok: false,
      status: 400,
      error: 'Only store can be registered',
    }
  }
  return requestv2.post(`/store/register`, _data, withSignal(options))
}

const post_group_register = async (_data, options = {}) => {
  if (Number(_data?.role) !== 3) {
    return {
      ok: false,
      status: 400,
      error: 'Only group can be registered',
    }
  }
  return requestv2.post(`/store/register`, _data, withSignal(options))
}

const post_store_register_verify = (_data, options = {}) => request.post(`/store/register/verify`, _data, withSignal(options))

const post_store_register_resend = (_data, options = {}) => request.post(`/store/register/resend`, _data, withSignal(options))

const check_registration_email = (_data, options = {}) => request.post(`/register/email/check`, _data, withSignal(options))

const post_employee_register = (_data, options = {}) => request.post(`/employee/register`, _data, withSignal(options))

const patch_store_onboard = (_data, _id, options = {}) => request.patch(`/v2/user/store/${_id}`, _data, withSignal(options))

const post_user_status = (_status, _location, _id, _previous, options = {}) => {
  const date = formatISO(new Date())
  return request.post(`/user/time/${_id}`, { status: _status, location: _location, logdate: date, previous: _previous }, withSignal(options))
}
const post_user_workmate_status = (_status, _location, _id, _previous, _workmate, options = {}) => {
  const date = formatISO(new Date())
  return request.post(`/user/workmate_time/${_id}`, {
    status: _status,
    location: _location,
    logdate: date,
    previous: _previous,
    workmate: _workmate,
  }, withSignal(options))
}
const remove_user = (_id, _uid, options = {}) => request.delete(`/store/${_id}/user/${_uid}`, null, withSignal(options))

const patch_user_onboard = (_data, _id, options = {}) => request.patch(`/user/${_id}`, _data, withSignal(options))

const get_user_records = (_id, limit = null, options = {}) => {
  const config = withSignal(options)
  if (limit) {
    return requestv2.get(`/user/records/${_id}`, { limit }, config)
  }
  return request.get(`/user/records/${_id}`, null, config)
}

const get_user_records_range = (_id, _date, options = {}) => request.get(`/user/records/${_id}/${_date}`, null, withSignal(options))
/*
const get_user_records_range_v2 = (_id, _startDate, _endDate) =>
  request.get(`/user/recordsv2/${_id}/${_startDate}/${_endDate}`)*/

const get_user_records_range_v2 = (_id, _startDate, _endDate, options = {}) =>
  requestv2.get(`/user/recordsv2/${_id}/${_startDate}/${_endDate}`, null, withSignal(options))

const get_day_quote = (options = {}) => request.get(`https://type.fit/api/quotes`, null, withSignal(options))

const post_branch = (form_data, options = {}) => request.post('/store/branch', form_data, withSignal(options))

const post_store_distance = (_location, options = {}) => request.post('/user/storedistance', _location, withSignal(options))

const get_user_branch = (_id, options = {}) => request.get(`/store/branch/${_id}`, null, withSignal(options))

const patch_user_location = (_id, _data, options = {}) => requestv2.patch(`/user/updateStore/${_id}`, _data, withSignal(options))

const get_user_locationv1 = (options = {}) => request.get(`https://ipapi.co/json/`, null, withSignal(options))

const get_user_ip = (options = {}) => request.get(`http://ipwho.is/`, null, withSignal(options))

const get_user_company = (options = {}) => request.get(`/users/company`, null, withSignal(options))

const get_record_bystore = (data, fomattedDate, options = {}) => request.post(`/store/records/${fomattedDate}`, data, withSignal(options))

const update_user_password = (_id, _password, options = {}) =>
  requestv2.get(`/user/updatePass/${_id}/${encodeURIComponent(_password)}`, null, withSignal(options))

const get_user_locationv2 = (options = {}) =>
  request.get(`https://ipapi.co/json/?key=HVywqXALKicFBIeVM2FDoZGwIvVQ3Ys0RtHTH4Cw67P6O2zoOY`, null, withSignal(options))

const get_storyblok_version = (options = {}) =>
  request.get(`https://api.storyblok.com/v1/cdn/spaces/announcement?version=published&token=cTyDJj4G8trQivtm2BFmpQtt`, null, withSignal(options))

const get_storyblok_banners = (version, options = {}) =>
  request.get(
    `https://api.storyblok.com/v1/cdn/stories/announcement?version=published&token=cTyDJj4G8trQivtm2BFmpQtt&cv=${version.data.space.version}`,
    null,
    withSignal(options),
  )

const get_store_location = (company, options = {}) => requestv2.get(`/store/location/${encodeURIComponent(company)}`, null, withSignal(options))

const set_company_coc = (data, options = {}) => request.post('/coc', data, withSignal(options))

const remove_company_coc = (data, options = {}) => requestv2.post('/coc/remove', data, withSignal(options))

const get_company_coc = (company, options = {}) => requestv2.post('/get/coc', company, withSignal(options))

const post_schedule = (data, options = {}) => requestv2.post('/schedule', data, withSignal(options))

const post_schedule_batch = (data, options = {}) =>
  requestv2.post('/schedule/batch', data, withSignal(options))

const post_schedule_admin = (data, options = {}) => request.post('/v2/schedule', data, withSignal(options))

const get_schedule = (_id, options = {}) => request.get(`/schedule/${_id}`, null, withSignal(options))

const get_all_schedule = (data, options = {}) => request.post(`/all/schedule/`, data, withSignal(options))

const get_verification_code = (data, options = {}) => request.post(`/user/reset`, data, withSignal(options))

const set_verification_code = (data, options = {}) => request.post(`/user/verify`, data, withSignal(options))

const set_new_password = (data, options = {}) => request.post(`/user/newPass`, data, withSignal(options))

const request_upload_url = (data, options = {}) => request.post(`aws/upload`, data, withSignal(options))

const post_store_announcement = (data, options = {}) => request.post(`announcement/upload`, data, withSignal(options))

const get_store_announcement_deduped = createDedupedRequest(
  (p) => `announcements:${p.store ?? ''}`,
  (p) => {
    const { signal, ...body } = p
    return requestv2.post(`announcements`, body, withSignal({ signal }))
  }
)
const get_store_announcement = (data, options = {}) => get_store_announcement_deduped({ ...data, ...options })

const edit_store_announcement = (data, options = {}) => request.post(`edit/announcements`, data, withSignal(options))

const toggle_announcement = (data, options = {}) => request.post(`/toggle/announcement`, data, withSignal(options))

const get_store_announcement_by_id = (id, options = {}) => request.get(`announcements/${id}`, null, withSignal(options))

const delete_store_announcement_by_id = (id, options = {}) => request.get(`delete/announcements/${id}`, null, withSignal(options))

const post_subscription = (data, options = {}) => request.post(`/subscription`, data, withSignal(options))

const get_subscription = (data, options = {}) => request.post(`/get/subscription`, data, withSignal(options))

const get_subscriptions = (options = {}) => request.get(`/v2/subscriptions`, null, withSignal(options))

//const get_active_users = (data) => axios.post(`https://api-admin.sparkletimekeeping.com/api/active`, data)

const get_active_users = (data, options = {}) => requestv2.post(`/active`, data, withSignal(options))

const generate_payslip = (options = {}) => axios.get(`http://127.0.0.1:8000/api/payslip`, withSignal(options))

const get_payslip_info = (data, options = {}) => requestv2.post('/payslip-info', data, withSignal(options))

const get_timeAdjustment_otp = (data, options = {}) => request.post(`/otp/time-adjustment`, data, withSignal(options))

const post_timeAdjustment_otpVerify = (data, options = {}) => request.post('/otp/verification', data, withSignal(options))

const post_timeAdjustment_otpVerifyV2 = (data, options = {}) => request.post('/v2/otp/verification', data, withSignal(options))

const post_myschedule_otp = (data, options = {}) => request.post('/otp/myschedule/send', data, withSignal(options))

const get_videos_tutorial = (company, options = {}) => request.get(`/store/videos/${company}`, null, withSignal(options))

const post_videos_tutorial = (data, options = {}) => request.post('/store/video', data, withSignal(options))

const edit_video_tutorial = (id, data, options = {}) => request.put(`/store/${id}`, data, withSignal(options))

const delete_video_tutorial = (id, options = {}) => request.delete(`/store/video/${id}`, null, withSignal(options))

const get_checklist = (data, options = {}) => requestv2.post('/checklist', data, withSignal(options))

const post_checklist = (data, options = {}) => request.post(`/checklist/add`, data, withSignal(options))

const delete_checklist = (data, options = {}) => request.post(`/checklist/delete`, data, withSignal(options))

const verify_password = (data, options = {}) => request.post(`/verify/password`, data, withSignal(options))

const verify_phone = (data, options = {}) => request.post(`/verify/phone`, data, withSignal(options))

const set_MPIN = (data, options = {}) => request.post(`/set/MPIN`, data, withSignal(options))

const send_MPIN_change_otp = (data, options = {}) => request.post(`/auth/otp/mpin_change/send`, data, withSignal(options))

const get_store = (id, options = {}) => request.get(`/store/${id}`, null, withSignal(options))

const get_store_qr = (id, options = {}) => request.get(`/store/qr/${id}`, null, withSignal(options))

const get_employee_registration_qr = (data, options = {}) =>
  requestv2.post('/stores/employee-registration-qr/get', data, withSignal(options))

const regenerate_employee_registration_qr = (data, options = {}) =>
  requestv2.post('/stores/employee-registration-qr/regenerate', data, withSignal(options))

const update_email = (data, options = {}) => request.post(`/email/update`, data, withSignal(options))

// const get_breaklist = (data) => request.post(`/breaklist`, data)
//const get_breaklist = (data, options = {}) => requestv2.post('/breaklist', data, withSignal(options))

const get_breaklist_v2 = (data, options = {}) => requestv2.post('/v2/breaklist', data, withSignal(options))

const get_breaklistinfo = (data, options = {}) => requestv2.post('/v2/breaklistinfo', data, withSignal(options))

const post_save_breaklist = (data, options = {}) => requestv2.post('/save/breaklist', data, withSignal(options))

const get_store_breaklist = (data, options = {}) => requestv2.post('/list/breaklist', data, withSignal(options))

/*const get_schedule_v2 = (data) => request.post(`schedule/v2`, data)*/
const get_schedule_v2 = (data, options = {}) => requestv2.post('/schedule/v2', data, withSignal(options))

//const get_schedule_with_range = (data) => request.post(`/range/schedule`, data)
const get_schedule_with_range = (data, options = {}) =>
   requestv2.post('/range/schedule', data, withSignal(options))

const get_reports_for_breaklist = (data, options = {}) =>
  requestv2.post('/reports/breaklist', data, withSignal(options))

const delete_breaklist = (data, options = {}) => requestv2.post('/delete/breaklist', data, withSignal(options))

/*const approve_breaklist = (data) => request.post(`/approve/breaklist`, data)*/
const approve_breaklist = (data, options = {}) =>
  requestv2.post('/v2/approve/breaklist', data, withSignal(options))

const updateBreaklistEmailSettings = (data, options = {}) => request.patch('/user/settings/breaklist-email', data, withSignal(options))

const approve_breaklist_otp = (data, options = {}) => requestv2.post('/otp/breaklist', data, withSignal(options))

const get_user_records_range_v2_bystore = (data, options = {}) => request.post(`report/store`, data, withSignal(options))

const get_stores_dashboard = (data, options = {}) => requestv2.post('stores/dashboard', data, withSignal(options))

const get_stores_dashboard_breakdown = (data, options = {}) => requestv2.post('stores/dashboard/breakdown', data, withSignal(options))

const get_breaklist_pending = (data, options = {}) => request.post(`list/breaklist/pending`, data, withSignal(options))

const delete_schedule = (id, options = {}) => request.get(`delete/schedule/${id}`, null, withSignal(options))

const edit_schedule = (data, options = {}) => request.post(`edit/schedule`, data, withSignal(options))

const update_breaklist = (data, options = {}) => request.post(`update/breaklist`, data, withSignal(options))

const realign_breaklist_totals = (data, options = {}) =>
  requestv2.post('/breaklist/realign-totals', data, withSignal(options))

const get_schedule_v2_bystore = (data, options = {}) => {
  const storeRaw = data?.store
  const store =
    typeof storeRaw === 'string'
      ? storeRaw
      : (storeRaw?.storeName ?? storeRaw?.company ?? storeRaw?.name ?? '')

  const id = data?.id 

  const payload = { store, id, date: data?.date }
  
  return requestv2.post(`schedule/store`, payload, withSignal(options))
}

const get_schedule_cost = (data, options = {}) => requestv2.post('/schedule/cost', data, withSignal(options))

const get_user_by_store = (data, options = {}) => request.post(`store/user`, data, withSignal(options))

const get_new_store_deduped = createDedupedRequest(
  () => 'new-stores',
  (p) => request.get(`new/stores`, null, withSignal(p)),
  250
)
const get_new_store = (options = {}) => get_new_store_deduped(options)

const get_account_logs = (id, options = {}) => request.get(`logs/${id}`, null, withSignal(options))

const get_trial_default = (options = {}) => requestv2.get('/trial/default', null, withSignal(options))

const get_store_grouping_management = (options = {}) =>
  requestv2.get('/account-management/store-grouping', null, withSignal(options))

const move_store_to_group = (data, options = {}) =>
  requestv2.post('/account-management/store-grouping/move', data, withSignal(options))

const update_trial_default = (defaultTrialDays, options = {}) =>
  requestv2.patch('/trial/default', { defaultTrialDays }, withSignal(options))

const update_account_trial_days_left = (id, daysLeft, options = {}) =>
  requestv2.patch(`/trial/account/${id}`, { daysLeft }, withSignal(options))

const getTimeAdjustmentLogs = (params = {}, options = {}) =>
  requestv2.get('time-adjustment-logs', params, withSignal(options))

const approve_new_store = (id, options = {}) => requestv2.get(`approve/store/${id}`, null, withSignal(options))

const delete_group_store = async (data, options = {}) => {
  const response = await request.post(`delete/store/group`, data, withSignal(options))
  if (response?.ok || (response?.status >= 200 && response?.status < 300)) {
    clearGroupStoreCacheImpl(data?.id)
  }
  return response
}

const decline_new_store = (id, options = {}) => request.get(`decline/store/${id}`, null, withSignal(options))

// Group store: centralized via groupStoreDispatch (single fetch per id, dispatch-based); API path in users.js
registerGroupStoreFetcher((id, options = {}) => request.get(`group/store/${id}`, null, withSignal(options)))
const get_group_store = (id, options = {}) => {
  // groupStoreDispatch intentionally de-dupes & caches globally, but doesn't support per-consumer abort signals.
  // If a signal is provided, bypass the dispatcher so the underlying request can be aborted cleanly.
  if (options?.signal != null) {
    return request.get(`group/store/${id}`, null, withSignal(options))
  }
  return getGroupStoreImpl(id)
}
const getCachedGroupStore = getCachedGroupStoreImpl
const clearGroupStoreCache = clearGroupStoreCacheImpl

const get_group_store_v2 = (id, options = {}) => requestv2.get(`v2/group/store/${id}`, null, withSignal(options))

const post_group_store = async (data, options = {}) => {
  if (!data?.storeid && data?.company) {
    const companyCheck = await check_registration_company(
      { company: data.company, role: 1 },
      options
    )
    if (!companyCheck?.ok) return companyCheck
    if (companyCheck?.data?.available === false) {
      return {
        ...companyCheck,
        ok: false,
        status: 409,
      }
    }
  }
  const response = await request.post(`register/store`, data, withSignal(options))
  if (response?.ok || (response?.status >= 200 && response?.status < 300)) {
    clearGroupStoreCacheImpl(data?.uid)
  }
  return response
}

const change_account = (options = {}) => request.post('/change/account', {}, withSignal(options))

const get_available_store_email = (data, options = {}) => request.post('/store/email/available', data, withSignal(options))

const get_user_bystore = (data, options = {}) => request.post(`user/store`, data, withSignal(options))

const get_store_profile_account = (company, options = {}) => requestv2.post(`store/user`, { company, role: 1 }, withSignal(options))

const get_store_profile_account_fallback = (store, options = {}) => requestv2.post(`user/store`, { store, role: 1 }, withSignal(options))

const get_archiveduser_bystore = (data, options = {}) => request.post(`archiveduser/store`, data, withSignal(options))

const edit_user_profile = (id, data, options = {}) => request.post(`edit/profile/${id}`, data, withSignal(options))

const edit_user_dynamic_profile = (id, data, options = {}) => requestv2.post(`edit/dynamic/profile/${id}`, data, withSignal(options))

const edit_user_company = (id, data, options = {}) => request.post(`edit/company/${id}`, data, withSignal(options))

const get_active_fcm_token = async (id, options = {}) => request.get(`/users/fcm/${id}`, null, withSignal(options))

const get_specific_schedule = (id, options = {}) => request.get(`/specific/schedule/${id}`, null, withSignal(options))

const get_specific_schedule_with_date = (data, options = {}) => request.post(`/specific/schedule/with/date`, data, withSignal(options))

const get_specific_schedule_with_date_v2_deduped = createDedupedRequest(
  (p) => `schedule:${p?.id ?? ''}:${p?.date ?? ''}`,
  (p) => {
    const { signal, ...body } = p
    return request.post(`/v2/specific/schedule/with/date`, body, withSignal({ signal }))
  }
)
const get_specific_schedule_with_date_v2 = (data, options = {}) => get_specific_schedule_with_date_v2_deduped({ ...data, ...options })

const post_schedule_employee = (data, options = {}) => request.post(`/specific/schedule/employee`, data, withSignal(options))

const patch_schedule_employee = (data, options = {}) =>
  requestv2.patch('/specific/schedule/employee', data, withSignal(options))

const update_schedule_remarks = (data, options = {}) =>
  request.patch('/specific/schedule/employee/remarks', data, withSignal(options))

const get_payslip_v3 = (data, options = {}) => axios.post('https://payroll-api.sparkletimekeeping.com/api/payroll/info/v3', data, withSignal(options))

const get_groups = (options = {}) => request.get('/groups', null, withSignal(options))
// const get_groups = () => axios.get('https://api-admin.sparkletimekeeping.com/api/groups')

const get_stores = (data, options = {}) => requestv2.post('/stores', data, withSignal(options))

const get_stores_v2 = (data, options = {}) => requestv2.post('/v2/stores', data, withSignal(options))

const get_approved_stores_dashboard = (params = {}, options = {}) =>
  requestv2.get('/v2/stores/approved', params, withSignal(options))

const archive_store_account = (storeId, options = {}) =>
  requestv2.patch(`/v2/stores/${storeId}/archive`, {}, withSignal(options))

const archive_store_account_by_name = (storeName, options = {}) =>
  requestv2.patch('/v2/stores/archive-by-name', { storeName }, withSignal(options))

const restore_store_account = (storeId, options = {}) =>
  requestv2.patch(`/v2/stores/${storeId}/restore`, {}, withSignal(options))

const delete_group = (id, options = {}) => request.get(`/remove/group/${id}`, null, withSignal(options))

const get_feedbacks = (page = 1, limit = 20, search = '', startDate = '', endDate = '', options = {}) =>
  requestv2.get(`/feedback`, {
    page,
    limit,
    search: search || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }, withSignal(options))

const get_feedbacks_by_rating = (rating, page = 1, limit = 20, search = '', startDate = '', endDate = '', options = {}) =>
  requestv2.get(`/feedback/${encodeURIComponent(rating)}`, {
    page,
    limit,
    search: search || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }, withSignal(options))

const get_feedbacks_stats = (rating, startDate = '', endDate = '', options = {}) =>
  requestv2.get(`/feedback/stats`, {
    ...(rating != null ? { rating } : {}),
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }, withSignal(options))

const download_feedbacks = async (options = {}) => {
  try {
    const response = await requestv2.get('/feedback/download', null, {
      responseType: 'blob',
      ...withSignal(options),
    })

    const blob = new Blob([response.data], {type: 'text/csv'})
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = 'feedbacks.csv' // 👈 the filename you want
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Download failed', err)
  }
}

const get_features_deduped = createDedupedRequest(
  (p) => `features:${p?.store ?? ''}`,
  (p) => {
    const { signal, ...body } = p
    return requestv2.post('/check/feature', body)
  }
)
const get_features = (data, options = {}) => get_features_deduped({ ...data, ...options })

const submit_feedback = async (id, feedback, options = {}) => {
  return await request.post(`/user/${id}/feedback`, feedback, withSignal(options))
}

const subscribe_fcm_token = async (id, fcmToken, device, deviceId, deviceInfo, options = {}) =>
  request.post(`/users/fcm/${id}/register`, { fcmToken, device, deviceId, deviceInfo }, withSignal(options))

const deleteDeviceFcmToken = async (id, fcmToken, options = {}) => {
  return await request.delete(`/users/fcm/${id}/unregister`, { fcmToken }, withSignal(options))
}

const change_password = async (data, options = {}) => {
  try {
    const response = await axios.post('/api/users/change-password', data, withSignal(options))
    return response
  } catch (error) {
    return error.response
  }
}

const edit_profile_pic = (data, options = {}) => request.post(`/user/profile_pic/edit`, data, withSignal(options))

const send_otp_for_mobile_change = (data, options = {}) =>
  requestv2.post('/user/otp/mobile/send', data, withSignal(options))

const verify_mobile_change_otp = (data, options = {}) => request.post(`/user/otp/mobile/verify`, data, withSignal(options))

const send_email_change_otps = (data, options = {}) => request.post(`/user/otp/email/send`, data, withSignal(options))

const verify_email_change_otp = (data, options = {}) => request.post(`/user/otp/email/verify`, data, withSignal(options))

const get_theme_song = (data, options = {}) => request.post('/stores/theme-song/get', data, withSignal(options))

const set_theme_song = (data, options = {}) => request.post('/stores/theme-song/set', data, withSignal(options))

const remove_theme_song = (data, options = {}) => requestv2.post('/stores/theme-song/remove', data, withSignal(options))

const get_employee_handbook = (data, options = {}) => request.post('/stores/employee-handbook/get', data, withSignal(options))

const set_employee_handbook = (data, options = {}) => request.post('/stores/employee-handbook/set', data, withSignal(options))

const remove_employee_handbook = (data, options = {}) => requestv2.post('/stores/employee-handbook/remove', data, withSignal(options))

const get_mission_vision = (data, options = {}) => request.post('/stores/mission-vision/get', data, withSignal(options))

const set_mission_vision = (data, options = {}) => request.post('/stores/mission-vision/set', data, withSignal(options))

const remove_mission_vision = (data, options = {}) => requestv2.post('/stores/mission-vision/remove', data, withSignal(options))

const send_timein_otp = (data, options = {}) =>
  requestv2.post('/v2/stores/time-in-qr/send-otp', data, withSignal(options))

const generate_timein_otp = (data, options = {}) =>
  requestv2.post('/stores/time-in-qr/generate-otp', data, withSignal(options))

const generate_timein_qr = (data, options = {}) =>
  requestv2.post('/v2/stores/time-in-qr/generate', data, withSignal(options))

const get_timein_qr = (data, options = {}) => request.post('/stores/time-in-qr/get', data, withSignal(options))

const get_existing_internal_timein_otp = (data, options = {}) => request.post('/stores/time-in-qr/get-existing-otp', data, withSignal(options))

const check_breaklist = (data, options = {}) => request.post('/check/breaklist', data, withSignal(options))

const check_breaklist_batch = (data, options = {}) => request.post('/check/breaklist/batch', data, withSignal(options))

const get_action_logs = (data, options = {}) => axios.post('https://api-cronjob.sparkletimekeeping.com/api/action-logs', data, withSignal(options))

const post_action_log = (data, options = {}) => axios.post('https://api-cronjob.sparkletimekeeping.com/api/action-logs/create', data, withSignal(options))

const get_sforms_form = (id, options = {}) => axios.get(`https://sapex-backend.onrender.com/api/preemploymentprocess/${id}/published`, withSignal(options))

const get_sforms_response = (data, options = {}) => axios.get(`https://sapex-backend-templates.onrender.com/api/preemployment/${data.formId}/single-response?${data.fieldId}=${data.email}`, withSignal(options))

const post_holiday = (data, options = {}) => request.post('/v2/holiday', data, withSignal(options))

const get_holidays = (options = {}) => request.get('/holidays', null, withSignal(options))

const get_holidays_v2 = (data, options = {}) => requestv2.post('/holidays/v2', data, withSignal(options))

const delete_holiday = (id, options = {}) => request.get(`/delete/holiday/${id}`, null, withSignal(options))


const get_training_materials = (data, options = {}) => request.post('/training-materials/get', data, withSignal(options))

const create_training_material = (data, options = {}) => request.post('/training-materials/create', data, withSignal(options))

const update_training_material = (id, data, options = {}) => request.put(`/training-materials/update/${id}`, data, withSignal(options))

const delete_training_material = (id, options = {}) => request.delete(`/training-materials/delete/${id}`, null, withSignal(options))

const get_training_material_by_id = (id, options = {}) => request.get(`/training-materials/${id}`, null, withSignal(options))

const rate_training_material = (id, data, options = {}) => request.post(`/training-materials/${id}/rate`, data, withSignal(options))

const complete_training_material = (id, data, options = {}) => request.post(`/training-materials/${id}/complete`, data, withSignal(options))

const get_my_request = (data, options = {}) => request.post(`/myrequest/get`, data, withSignal(options))

const delete_my_request = (id, options = {}) => request.delete(`/myrequest/${id}`, null, withSignal(options))

const update_my_request = (data, options = {}) => request.post(`/myrequest/update`, data, withSignal(options))

const get_my_request_status = (data, options = {}) => request.post(`/myrequeststatus/get`, data, withSignal(options))

const get_my_request_status_personnel = (data, options = {}) => request.post(`/myrequeststatus/personnel`, data, withSignal(options))

const get_my_request_status_counts = (data, options = {}) => requestv2.post('/myrequeststatus/personnel/counts', data, withSignal(options))

const post_my_request_status = (data, options = {}) => request.post(`/myrequeststatus`, data, withSignal(options))

const post_my_request = (data, options = {}) => request.post(`/myrequest`, data, withSignal(options))

const update_my_request_status = (data, options = {}) => request.post(`/myrequeststatus/update`, data, withSignal(options))

// Process-flow get: single endpoint used by dispatcher (centralized fetch in processFlowDispatch.js)
registerFetcher((store, options = {}) => requestv2.post('/myrequest/process-flow/get', { store }, withSignal(options)))

// Process-flow: centralized via processFlowDispatch (single fetch per store, dispatch-based)
const get_request_process_flow = (data) => {
  const store = typeof data?.store === 'string' ? data.store.trim() : ''
  const options = data?.signal != null ? { signal: data.signal } : {}
  return getProcessFlow(store, options)
}
const getCachedProcessFlow = getCachedProcessFlowImpl
const clearProcessFlowCache = clearProcessFlowCacheImpl

const update_request_process_flow = (data, options = {}) => requestv2.post('/myrequest/process-flow/update', data, withSignal(options))

// ========================= My Request (v2) ========================= //
const validate_request_process_flow_v2 = (data, options = {}) =>
  requestv2.post('/v2/myrequest/process-flow/validate', data, withSignal(options))

const update_request_process_flow_v2 = (data, options = {}) =>
  requestv2.post('/v2/myrequest/process-flow/update', data, withSignal(options))

const get_process_flow_responses_v2 = (processFlowId, params = {}, options = {}) =>
  requestv2.get(`/v2/myrequest/process/${processFlowId}/responses`, params, withSignal(options))

const get_process_flow_responses_bulk_v2 = (processFlowId, data, options = {}) =>
  requestv2.post(`/v2/myrequest/process/${processFlowId}/responses/bulk`, data, withSignal(options))

const get_process_flow_store_summary_v2 = (processFlowId, data, options = {}) =>
  requestv2.post(`/v2/myrequest/process/${processFlowId}/store-summary`, data, withSignal(options))

const get_process_flow_store_summary_detail_v2 = (processFlowId, data, options = {}) =>
  requestv2.post(`/v2/myrequest/process/${processFlowId}/store-summary/detail`, data, withSignal(options))

const get_employee_process_responses_v2 = (processFlowId, params = {}, options = {}) =>
  requestv2.get(`/v2/myrequest/employee/process/${processFlowId}/responses`, params, withSignal(options))

const get_employee_process_responses_bulk_v2 = (processFlowId, data, options = {}) =>
  requestv2.post(`/v2/myrequest/employee/process/${processFlowId}/responses/bulk`, data, withSignal(options))

const sync_employee_responses_v2 = (data, options = {}) =>
  requestv2.post('/v2/myrequest/employee/sync-responses', data, withSignal(options))

const change_my_request_status_v2 = (data, options = {}) => requestv2.post('/v2/myrequeststatus/change-status', data, withSignal(options))

const get_my_request_status_logs_v2 = (id, options = {}) => requestv2.get(`/v2/myrequeststatus/${id}/logs`, null, withSignal(options))

const rank_announcement = (data, options = {}) => request.post(`/announcement/rank`, data, withSignal(options))

const set_ui_version = (id, version, options = {}) => request.patch(`/user/${id}/ui-version`, { version }, withSignal(options))

// Schedule Cost OTP
const send_schedule_cost_otp = (data, options = {}) => requestv2.post('/otp/schedule-cost/send', data, withSignal(options))
const verify_schedule_cost_otp = (data, options = {}) => requestv2.post('/otp/schedule-cost/verify', data, withSignal(options))

// ========================= Time In/Out Reminder ========================= //
const buildReminderParams = (data) => {
  const params = { scope: data?.scope }
  if (data?.scope === 'group' && data?.groupManagerId) params.groupManagerId = data.groupManagerId
  if (data?.scope === 'store' && data?.storeName) params.storeName = data.storeName
  if (data?.scope === 'employee' && data?.employeeId) params.employeeId = data.employeeId
  return params
}

const get_time_in_out_reminder_config = (data, options = {}) =>
  requestv3.get('/reminders/time-in-out', buildReminderParams(data), withSignal(options))

const put_time_in_out_reminder_config = (data, options = {}) =>
  requestv3.put('/reminders/time-in-out', data, withSignal(options))

const delete_time_in_out_reminder_config = (data, options = {}) =>
  requestv3.delete('/reminders/time-in-out', buildReminderParams(data), withSignal(options))

const get_time_in_out_reminder_features_deduped = createDedupedRequest(
  (p) => `reminder-features:${p?.store ?? ''}`,
  (p) => {
    const { signal, ...body } = p
    return requestv3.post('/check/feature', body, withSignal(p))
  }
)
const get_time_in_out_reminder_features = (data, options = {}) =>
  get_time_in_out_reminder_features_deduped({ ...data, ...options })

const get_time_in_out_reminder_subscription = (data, options = {}) =>
  requestv3.post('/get/subscription', data, withSignal(options))

const _expObject = {
  get_user,
  get_user_by_id,
  seedUserCacheFromLoginPayload,
  get_user_status,
  check_user_archived,
  get_user_archived,
  get_users,
  get_users_archived,
  archive_user,
  restore_user,
  post_store_register,
  post_store_register_verify,
  post_store_register_resend,
  check_registration_email,
  check_registration_company,
  post_employee_register,
  patch_store_onboard,
  post_user_status,
  patch_user_onboard,
  get_user_records,
  get_user_records_range,
  get_day_quote,
  remove_user,
  post_branch,
  get_user_branch,
  post_user_workmate_status,
  get_user_records_range_v2,
  patch_user_location,
  get_user_locationv1,
  get_user_locationv2,
  get_user_ip,
  get_limited_users,
  get_users_bydate,
  create_new_password,
  validate_password,
  get_limited_usersV2,
  get_record_by_id,
  delete_record_by_id,
  delete_last_record_by_id,
  delete_demo_last_record,
  update_record_by_id,
  get_user_company,
  get_record_bystore,
  update_user_password,
  post_store_distance,
  get_store_location,
  get_storyblok_banners,
  get_storyblok_version,
  set_company_coc,
  remove_company_coc,
  get_company_coc,
  get_theme_song,
  set_theme_song,
  remove_theme_song,
  get_employee_handbook,
  set_employee_handbook,
  remove_employee_handbook,
  get_mission_vision,
  set_mission_vision,
  remove_mission_vision,
  post_schedule,
  post_schedule_batch,
  post_schedule_admin,
  get_schedule,
  get_all_schedule,
  set_verification_code,
  get_verification_code,
  set_new_password,
  request_upload_url,
  post_store_announcement,
  get_store_announcement,
  edit_store_announcement,
  get_store_announcement_by_id,
  delete_store_announcement_by_id,
  post_subscription,
  get_subscription,
  get_active_users,
  get_payslip_info,
  generate_payslip,
  get_timeAdjustment_otp,
  post_timeAdjustment_otpVerify,
  post_timeAdjustment_otpVerifyV2,
  get_videos_tutorial,
  post_videos_tutorial,
  edit_video_tutorial,
  delete_video_tutorial,
  get_checklist,
  post_checklist,
  delete_checklist,
  verify_password,
  get_store,
  get_store_qr,
  get_employee_registration_qr,
  regenerate_employee_registration_qr,
  update_email,
  verify_phone,
  set_MPIN,
  send_MPIN_change_otp,
  get_breaklist_v2,
  get_breaklistinfo,
  post_save_breaklist,
  get_store_breaklist,
  get_schedule_v2,
  get_schedule_with_range,
  get_reports_for_breaklist,
  delete_breaklist,
  approve_breaklist,
  delete_schedule,
  edit_schedule,
  get_active_fcm_token,
  subscribe_fcm_token,
  deleteDeviceFcmToken,
  get_new_store,
  approve_new_store,
  decline_new_store,
  edit_user_profile,
  edit_user_company,
  get_account_logs,
  get_store_grouping_management,
  move_store_to_group,
  get_trial_default,
  update_trial_default,
  update_account_trial_days_left,
  getTimeAdjustmentLogs,
  get_group_store,
  getCachedGroupStore,
  clearGroupStoreCache,
  get_group_store_v2,
  post_group_store,
  change_account,
  get_available_store_email,
  get_user_bystore,
  get_store_profile_account,
  get_store_profile_account_fallback,
  get_archiveduser_bystore,
  get_schedule_v2_bystore,
  get_schedule_cost,
  get_user_records_range_v2_bystore,
  get_stores_dashboard,
  get_stores_dashboard_breakdown,
  get_breaklist_pending,
  update_breaklist,
  realign_breaklist_totals,
  delete_group_store,
  get_specific_schedule,
  get_specific_schedule_with_date,
  get_specific_schedule_with_date_v2,
  post_schedule_employee,
  change_password,
  get_user_by_store,
  post_myschedule_otp,
  edit_profile_pic,
  send_otp_for_mobile_change,
  verify_mobile_change_otp,
  send_email_change_otps,
  verify_email_change_otp,
  patch_schedule_employee,
  update_schedule_remarks,
  get_groups,
  get_stores,
  get_stores_v2,
  get_approved_stores_dashboard,
  archive_store_account,
  archive_store_account_by_name,
  restore_store_account,
  get_subscriptions,
  delete_group,
  get_features,
  submit_feedback,
  send_timein_otp,
  generate_timein_qr,
  generate_timein_otp,
  get_timein_qr,
  get_existing_internal_timein_otp,
  get_limited_usersV3,
  get_limited_usersV4,
  check_breaklist,
  check_breaklist_batch,
  get_feedbacks,
  get_feedbacks_by_rating,
  get_feedbacks_stats,
  download_feedbacks,
  updateBreaklistEmailSettings,
  approve_breaklist_otp,
  get_action_logs,
  post_action_log,
  post_holiday,
  get_holidays,
  delete_holiday,
  get_training_materials,
  create_training_material,
  update_training_material,
  delete_training_material,
  get_training_material_by_id,
  rate_training_material,
  complete_training_material,
  toggle_announcement,
  rank_announcement,
  set_ui_version,
  get_payslip_v3,
  send_schedule_cost_otp,
  verify_schedule_cost_otp,
  get_my_request,
  get_sforms_form,
  get_sforms_response,
  post_my_request_status,
  get_my_request_status,
  update_my_request_status,
  post_my_request,
  get_my_request_status_personnel,
  get_my_request_status_counts,
  update_my_request,
  delete_my_request,
  get_request_process_flow,
  getCachedProcessFlow,
  clearProcessFlowCache,
  clearUserCache,
  update_request_process_flow,
  validate_request_process_flow_v2,
  update_request_process_flow_v2,
  get_process_flow_responses_v2,
  get_process_flow_responses_bulk_v2,
  get_process_flow_store_summary_v2,
  get_process_flow_store_summary_detail_v2,
  change_my_request_status_v2,
  get_my_request_status_logs_v2,
  get_employee_process_responses_v2,
  get_employee_process_responses_bulk_v2,
  sync_employee_responses_v2,
  edit_user_dynamic_profile,
  archive_user_v2,
  get_holidays_v2,
  get_time_in_out_reminder_config,
  put_time_in_out_reminder_config,
  delete_time_in_out_reminder_config,
  get_time_in_out_reminder_features,
  get_time_in_out_reminder_subscription,
  post_group_register
}

export default _expObject
